'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { v4 as uuid } from 'uuid'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  Conversation,
  ConversationNew,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useConversationNavigator } from '@marketplace-web/messaging/navigation-feature'
import {
  openOfferScreenEvent,
  makeOfferEvent,
  startConversationEvent,
  getOfferRequestOptions,
  OfferPriceSuggestionOption,
  OfferRequestOptionsModel,
  transformOfferRequestOptions,
  createOfferRequest,
  CUSTOM_OFFER_PRICE_OPTION_INDEX,
} from '@marketplace-web/offers/price-offers-data'

import { BundleTransactionModel } from '@marketplace-web/bundles/items-bundle-data'

import BuyerOfferRequestDialog from '../../BuyerOfferRequestDialog'
import { OfferFormModel } from '../../types'
import BuyerOfferRequestsExceededModal from '../../BuyerOfferRequestsExceededModal'
import { OfferFormField } from '../../constants'

type CreateOfferUiState = {
  uiState: UiState
  error?: string
}

type Props = {
  isBundlingDialogOpen?: boolean
  isOpen: boolean
  currency: string
  bundleTransaction?: BundleTransactionModel | null
  conversation?: Conversation | ConversationNew
  onClose: () => void
  itemInfo: {
    itemId: string
    itemIds: Array<number>
    itemTitle?: string
    itemThumbnailUrl: string | null
    itemPrice: string
    sellerId: number
  }
  onSkipNavigationWarning?: () => void
}

// TODO: Refactor this component to be used with single and multiple items
const ItemBuyerOfferModal = ({
  isBundlingDialogOpen = true,
  isOpen,
  itemInfo: { itemId, itemIds, itemTitle, itemThumbnailUrl, itemPrice, sellerId },
  onClose,
  currency,
  bundleTransaction,
  conversation,
  onSkipNavigationWarning,
}: Props) => {
  const formMethods = useForm<OfferFormModel>({ mode: 'onChange' })
  const { reset, setError, getValues, handleSubmit } = formMethods

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { track } = useTracking()
  const { locale } = useIntl()
  const session = useSession()

  const screenName = session.screen
  const userId = session.user?.id

  const [createOfferUi, setCreateOfferUi] = useState<CreateOfferUiState>({
    uiState: UiState.Idle,
  })
  const [offerRequestSessionId, setOfferRequestSessionId] = useState<string>()
  const [offerRequestOptions, setOfferRequestOptions] = useState<OfferRequestOptionsModel>()
  const [selectedOfferRequestPriceSuggestion, setSelectedOfferRequestPriceSuggestion] =
    useState<OfferPriceSuggestionOption>()

  const conversationNavigator = useConversationNavigator()
  const conversationNavigationStartMark = useRef<
    ReturnType<(typeof conversationNavigator)['createNavigationStartMark']> | undefined
  >(undefined)

  const offerPriceSuggestions = offerRequestOptions?.offerSuggestions
  const transactionId =
    conversation?.transactionId || conversation?.transaction?.id || bundleTransaction?.id
  const conversationId =
    conversation?.id || conversation?.transaction?.id || bundleTransaction?.conversationId

  const handleError = useCallback(
    (message: string) => {
      setError(OfferFormField.Offer, { type: 'manual', message })
      setIsSubmitting(false)
    },
    [setError],
  )

  useEffect(() => {
    if (!isOpen) return

    setOfferRequestSessionId(uuid())
  }, [isOpen, setOfferRequestSessionId])

  useEffect(() => {
    if (!isOpen) return

    reset({
      [OfferFormField.Offer]: '',
    })
  }, [reset, isOpen, offerRequestOptions])

  useEffect(() => {
    if (!isBundlingDialogOpen) return

    const fetchOfferRequestOptions = async () => {
      const response = await getOfferRequestOptions({
        itemPrice: { amount: itemPrice, currencyCode: currency },
        sellerId,
      })

      if ('errors' in response) return

      const transformedOfferRequestOptions = transformOfferRequestOptions(response.request_options)

      setOfferRequestOptions(transformedOfferRequestOptions)

      const isOfferGuidanceEnabled = !!response.request_options.offer_suggestions

      if (isOfferGuidanceEnabled) {
        setSelectedOfferRequestPriceSuggestion({ isCustom: true })
      }
    }

    fetchOfferRequestOptions()
  }, [sellerId, itemPrice, currency, isOpen, isBundlingDialogOpen])

  const isBundlingScreen = screenName === 'bundling'

  useEffect(() => {
    if (isBundlingScreen) return

    if (createOfferUi.uiState === UiState.Success && conversationId && userId) {
      track(startConversationEvent({ userId, itemId }))

      conversationNavigator.navigate(conversationId, {
        navigationStartMark: conversationNavigationStartMark.current,
      })
    }

    if (createOfferUi.uiState === UiState.Failure && createOfferUi.error) {
      handleError(createOfferUi.error)
    }
  }, [
    userId,
    itemId,
    track,
    handleError,
    conversationId,
    createOfferUi,
    isBundlingScreen,
    conversationNavigator,
  ])

  useEffect(() => {
    if (!isBundlingScreen) return

    if (createOfferUi.uiState === UiState.Success && conversationId && userId) {
      conversationNavigator.navigate(conversationId, {
        navigationStartMark: conversationNavigationStartMark.current,
      })
    }

    if (createOfferUi.uiState === UiState.Failure && createOfferUi.error) {
      handleError(createOfferUi.error)
    }
  }, [
    userId,
    handleError,
    createOfferUi.error,
    createOfferUi.uiState,
    conversationId,
    isBundlingScreen,
    conversationNavigator,
  ])

  useEffect(() => {
    if (!isOpen || !offerPriceSuggestions) return undefined

    return () => {
      setSelectedOfferRequestPriceSuggestion({
        isCustom: true,
      })
    }
  }, [isOpen, offerPriceSuggestions, setSelectedOfferRequestPriceSuggestion])

  useEffect(() => {
    if (!transactionId || !isBundlingScreen) return

    track(
      openOfferScreenEvent({
        isBuyer: true,
        currentPrice: Number(itemPrice),
        currencyCode: currency,
        openedFrom: 'bundle_summary',
        transactionId,
      }),
    )
  }, [transactionId, isBundlingScreen, track, itemPrice, currency])

  async function handleFormSubmit() {
    conversationNavigationStartMark.current = conversationNavigator.createNavigationStartMark()
    if (!transactionId) return

    setIsSubmitting(true)
    onSkipNavigationWarning?.()

    const formattedOfferValue = getValues().offer.replace(',', '.')

    const selectedOfferPriceSuggestionIndex = offerPriceSuggestions?.findIndex(
      suggestion => suggestion.price === selectedOfferRequestPriceSuggestion?.suggestion?.price,
    )
    const optionIndex = selectedOfferRequestPriceSuggestion?.isCustom
      ? CUSTOM_OFFER_PRICE_OPTION_INDEX
      : selectedOfferPriceSuggestionIndex

    setCreateOfferUi({ uiState: UiState.Pending })

    const response = await createOfferRequest({
      transactionId,
      price: formattedOfferValue,
      currency,
    })

    if ('errors' in response) {
      setCreateOfferUi({
        uiState: UiState.Failure,
        error: response?.errors?.[0]?.value,
      })

      return
    }

    const offerRequestId = 'offer_request' in response && response.offer_request.id
    const offerSuggestionOptionNumber = optionIndex ? optionIndex + 1 : null

    const offerEvent = makeOfferEvent({
      isBuyer: true,
      transactionId,
      offerRequestSessionId,
      offerSuggestionOptionNumber,
      offerRequestId: offerRequestId || undefined,
      offeredPrice: Number(formattedOfferValue),
      currentPrice: Number(itemPrice),
    })

    track(offerEvent)
    setCreateOfferUi({ uiState: UiState.Success })
  }

  const handleOnClose = () => {
    if (isSubmitting) return

    onClose()
  }

  const remainingOffers = offerRequestOptions?.remainingOfferCount

  if (remainingOffers === 0) {
    return (
      <BuyerOfferRequestsExceededModal
        show={isOpen}
        onClose={onClose}
        maxOfferCount={offerRequestOptions?.maxOfferCount}
      />
    )
  }

  if (!offerRequestOptions || !remainingOffers) return null

  const { feesReductionThreshold } = offerRequestOptions

  const thresholdPrice = feesReductionThreshold
    ? formatCurrencyAmount(feesReductionThreshold, locale)
    : null

  return (
    <FormProvider {...formMethods}>
      <BuyerOfferRequestDialog
        show={isOpen}
        isSubmitting={isSubmitting}
        minItemPrice={offerRequestOptions.minPrice}
        maxItemPrice={offerRequestOptions.maxPrice}
        maxDiscount={offerRequestOptions.maxDiscount}
        itemPrice={{
          amount: itemPrice,
          currencyCode: currency,
        }}
        itemId={Number(itemId)}
        itemTitle={itemTitle}
        itemThumbnailUrl={itemThumbnailUrl}
        orderItemIds={itemIds}
        thresholdPrice={thresholdPrice}
        remainingOffers={remainingOffers}
        offerPriceSuggestions={offerPriceSuggestions}
        onSubmit={handleSubmit(handleFormSubmit)}
        onClose={handleOnClose}
        transactionId={transactionId}
        offerRequestSessionId={offerRequestSessionId}
        selectedOfferRequestPriceSuggestion={selectedOfferRequestPriceSuggestion}
        onSelectOfferRequestPriceSuggestion={setSelectedOfferRequestPriceSuggestion}
      />
    </FormProvider>
  )
}

export default ItemBuyerOfferModal
